
import { Controller } from "@hotwired/stimulus";
import flatpickr from "flatpickr";
import {
  flatpickrLocale,
  protectFlatpickrTranslation
} from "../helpers/flatpickr_locale";

export default class FlatpickrRangesController extends Controller {
  static targets = ["label", "fpInput", "start", "end"];
  static values = {
    url: String,
    modalType: String,
  };

  connect() {
    this.activePreset = null;
    this.handleLanguageChange = this.languageChange.bind(this);
    document.addEventListener("itoons:language-change", this.handleLanguageChange);

    const isMobile = globalThis.matchMedia("(max-width: 768px)").matches;
    this.fp = flatpickr(this.element, {
      locale: flatpickrLocale(),
      mode: "range",
      showMonths: isMobile ? 1 : 2,
      dateFormat: "Y-m-d",
      clickOpens: false,
      closeOnSelect: false,
      disableMobile: true,
      monthSelectorType: "static",
      altFormat: "m-d-Y",
      maxDate: new Date(),
      onReady: (_d, _s, instance) => {
        protectFlatpickrTranslation(instance);
        if (instance.input.closest(".modal")) {
          instance.calendarContainer.classList.add("flatpickr-modal-calendar");
        }
        this.injectFooter(instance);
      },

      onChange: (dates) => {
        if (dates.length === 2) {
          this.activePreset = "Custom Range";
          this.highlightActive();
        }
      }
    });

    const startDate = this.startOfDay(new Date(`${this.startTarget.value}T00:00:00`));
    const endDate = this.startOfDay(new Date(`${this.endTarget.value}T00:00:00`));

    this.labelTarget.textContent = `${this.pretty(startDate)} - ${this.pretty(endDate)}`;
    this.activePreset = "Last 7 Days";
    this.highlightActive();
    this.resetHandler = this.reset.bind(this);
    globalThis.addEventListener("reset-date-range", this.resetHandler);
  }

  disconnect() {
    document.removeEventListener("itoons:language-change", this.handleLanguageChange);
    globalThis.removeEventListener("reset-date-range", this.resetHandler);
    this.fp?.destroy();
  }

  languageChange(event) {
    this.fp?.set("locale", flatpickrLocale(event.detail?.language));
  }

  reset() {
    // Clear Flatpickr selection
    this.fp.clear();

    // Clear hidden fields
    this.startTarget.value = "";
    this.endTarget.value = "";

    // Reset label
    this.labelTarget.textContent = "Custom Range";

    // Remove active preset
    this.activePreset = null;
    this.highlightActive();
  }

  preset(e) {
    e.preventDefault();
    const label = e.currentTarget.dataset.preset;

    this.activePreset = label;

    const [start, end] = this.presetRange(label);

    this.setRange(start, end);
    this.highlightActive();

    this.closeBootstrapDropdown();
    this.search();
  }

  custom(e) {
    e.preventDefault();
    this.activePreset = "Custom Range";
    this.highlightActive();
    this.closeBootstrapDropdown();

    this.fp.open();
    const base = this.fp.selectedDates[0] || this.fromYmd(this.startTarget.value) || new Date();
    this.fp.jumpToDate(base);
  }

  injectFooter(instance) {
    const calendar = instance.calendarContainer;
    if (!calendar || calendar.querySelector(".fp-footer")) return;

    const footer = document.createElement("div");
    footer.className = "fp-footer d-flex justify-content-end gap-2 p-3";

    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn btn-outline-secondary rounded-2";
    cancel.innerHTML = "<span>Cancel</span>";
    cancel.addEventListener("click", () => {
      instance.close();
    });

    const apply = document.createElement("button");
    apply.type = "button";
    apply.className = "btn bg-tiffany-blue text-dark rounded-2";
    apply.innerHTML = "<span>Apply</span>";
    apply.addEventListener("click", () => {
      const d = instance.selectedDates;

      if (d.length === 1) instance.setDate([d[0], d[0]], true);
      this.setRange(d[0], d[1]);
      instance.close();

      this.activePreset = "Custom Range";
      this.highlightActive();
      this.search();
    });

    footer.append(cancel, apply);
    calendar.appendChild(footer);
  }

  setRange(start, end) {
    this.startTarget.value = this.fmt(start);
    this.endTarget.value = this.fmt(end);
    this.labelTarget.textContent = `${this.pretty(start)} - ${this.pretty(end)}`;
  }

  highlightActive() {
    for (const el of this.element.querySelectorAll(".dropdown-item")) {
      el.classList.remove("active");
    }

    if (this.activePreset) {
      const el = this.element.querySelector(`.dropdown-item[data-preset="${CSS.escape(this.activePreset)}"]`);
      const elLink = this.element.querySelector(`.nav-link[data-preset="${CSS.escape(this.activePreset)}"]`);

      el?.classList.add("active");
      if (elLink) {
        $(this.element).closest(".nav-tabs").find(".nav-link").removeClass("active");
        $(this.element).closest(".bp-tabs").find(".nav-link").removeClass("active");
        elLink.classList.add("active");
      }
    }

  }

  closeBootstrapDropdown() {
    const toggle = this.element.querySelector("[data-bs-toggle='dropdown']");
    if (!toggle) return;
    const dropdown = bootstrap.Dropdown.getOrCreateInstance(toggle);
    dropdown.hide();
  }

  presetRange(label) {
    const today = this.startOfDay(new Date());
    const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
    const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
    const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

    switch (label) {
      case "Today": return [today, today];
      case "Yesterday": { const y = addDays(today, -1); return [y, y]; }
      case "Last 7 Days": return [addDays(today, -6), today];
      case "Last 30 Days": return [addDays(today, -29), today];
      case "This Month": return [startOfMonth(today), endOfMonth(today)];
      case "Last Month": {
        const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        return [startOfMonth(lm), endOfMonth(lm)];
      }
      default: return [today, today];
    }
  }

  startOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }

  fmt(d) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  pretty(d) {
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  }

  fromYmd(s) {
    if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

    async search() {
        const start = this.startTarget.value
        const end = this.endTarget.value
        if (!start || !end) return
        const walletController = this.application.getControllerForElementAndIdentifier($('[data-controller="wallet-filter"]')[0], 'wallet-filter')
        if (walletController) {
            walletController.range(start, end);
        } else {
            fetch(this.urlValue + "&start_date=" + start + "&end_date=" + end, {
                method: "GET",
                headers: {
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
                    "Accept": "text/vnd.turbo-stream.html",
                    "Content-Type": "application/x-www-form-urlencoded"
                }
            }).then(async (response) => {
                if (response.redirected) {
                    Turbo.visit(response.url)
                    return
                }

                const body = await response.text()
                Turbo.renderStreamMessage(body)
            })
        }
    }
}
